/**
 * Wisconsin State-Specific Diagnostic Questions
 * COMPLETE REBUILD - Using correct Wis. Admin. Code ch. DHS 133 citations
 * 
 * CRITICAL CORRECTIONS FROM ORIGINAL FILE:
 * 1. Changed all "HFS 133" to "DHS 133" (renumbered January 2009 per s. 13.92(4)(b)1, Stats.)
 * 2. License is valid INDEFINITELY (DHS 133.03(5)), not "every 2 years"
 * 3. Fixed all section-to-content mappings (original had completely wrong sections)
 * 
 * CORRECT DHS 133 STRUCTURE:
 * - DHS 133.01: Authority and Purpose
 * - DHS 133.02: Definitions
 * - DHS 133.03: Licensure
 * - DHS 133.04: Inspections
 * - DHS 133.05: Governance
 * - DHS 133.06: Administration
 * - DHS 133.07: Evaluation
 * - DHS 133.08: Patient Rights
 * - DHS 133.09: Acceptance and Discharge of Patients
 * - DHS 133.10: Services Provided
 * - DHS 133.11: Referrals
 * - DHS 133.12: Coordination with Other Providers
 * - DHS 133.13: Emergency Notification
 * - DHS 133.14: Skilled Nursing Services
 * - DHS 133.15: Therapy Services
 * - DHS 133.16: Medical Social Services
 * - DHS 133.17: Home Health Aide Services
 * - DHS 133.18: Supervisory Visits
 * - DHS 133.19: Services Under Contract
 * - DHS 133.20: Plan of Care
 * - DHS 133.21: Medical Records
 * 
 * RELATED REGULATIONS:
 * - Wis. Stat. § 50.49: Licensing and Regulation of Home Health Agencies
 * - Wis. Stat. § 50.065: Caregiver Background Checks
 * - Wis. Admin. Code ch. DHS 12: Caregiver Background Checks
 * - Wis. Admin. Code ch. DHS 13: Caregiver Misconduct Reporting
 * - 42 CFR Part 484: Home Health Services (Medicare CoPs)
 * 
 * VERIFIED SOURCES:
 * - https://docs.legis.wisconsin.gov/code/admin_code/dhs/110/133
 * - https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm
 * - https://www.dhs.wisconsin.gov/misconduct/backgroundchecks.htm
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const WISCONSIN_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & GOVERNANCE
  // ============================================
  {
    id: 'WI-A1',
    itemId: 'A1',
    stateCode: 'WI',
    title: 'Home Health Agency License Status',
    description: 'Wisconsin requires all home health agencies to be licensed by DHS under Wis. Admin. Code ch. DHS 133',
    severity: 'high',
    regulationCode: 'DHS 133.03',
    regulationName: 'Licensure Requirements',
    agencyName: 'Wisconsin Department of Health Services, Division of Quality Assurance',
    agencyAbbr: 'DHS DQA',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from the Wisconsin Department of Health Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Wisconsin HHA licenses are valid indefinitely unless suspended, revoked, or voluntarily surrendered per DHS 133.03(5)',
      },
      {
        id: 'q2',
        question: 'Have you submitted your most recent annual report to DHS with the required fee?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, submitted on schedule' },
          { value: 'pending', label: 'Due soon / in process' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'unsure', label: 'Not sure' },
        ],
        helpText: 'Annual reports required per DHS 133.03(9) - failure to file within 60 days may result in license revocation',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'current' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['overdue', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Wisconsin DHS Home Health Agency license is active with annual reports current per DHS 133.03.',
      atRisk: 'Your annual report is due soon. DHS 133.03(9) requires submission on schedule - failure to file within 60 days may result in license revocation.',
      nonCompliant: 'Operating without a valid DHS license or with overdue annual reports violates Wis. Stat. § 50.49 and DHS 133.03. Contact DHS DQA immediately at (608) 266-8481.',
      unknown: 'Verify your license status with DHS DQA at (608) 266-8481.',
    },
    stateNotes: [
      'Wisconsin HHA licenses are valid INDEFINITELY unless suspended, revoked, or surrendered (DHS 133.03(5))',
      'Annual reports with fees required per DHS 133.03(9)',
      'Failure to file annual report within 60 days of due date may result in revocation',
      'Application fee: $300; Annual fee: 0.25% of patient fee revenue (DHS 133.03(4)(d))',
      'Provisional license requires serving 10 patients (7 receiving skilled nursing) before regular licensure survey',
    ],
  },

  {
    id: 'WI-A2',
    itemId: 'A2',
    stateCode: 'WI',
    title: 'Governing Body Requirements',
    description: 'Wisconsin requires a governing body responsible for agency management per DHS 133.05',
    severity: 'high',
    regulationCode: 'DHS 133.05',
    regulationName: 'Governance',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a governing body that assumes full legal authority and responsibility?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per DHS 133.05(1)',
      },
      {
        id: 'q2',
        question: 'Does your agency have an advisory group that meets Wisconsin requirements?',
        inputType: 'checklist',
        options: [
          { value: 'physician', label: 'At least one practicing physician' },
          { value: 'rn', label: 'At least one registered nurse' },
          { value: 'other_disciplines', label: 'Appropriate representation from other professional disciplines' },
          { value: 'majority_external', label: 'Majority are persons who are neither owners nor employees' },
          { value: 'meets_annually', label: 'Meets at least annually' },
        ],
        helpText: 'Advisory group requirements per DHS 133.05(2)(a)-(b)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governance structure meets DHS 133.05 requirements including properly constituted advisory group.',
      atRisk: 'Advisory group may not meet all DHS 133.05(2) requirements. Must include physician, RN, with majority non-owners/employees.',
      nonCompliant: 'No governing body or inadequate advisory group violates DHS 133.05.',
      unknown: 'Review governance requirements under DHS 133.05.',
    },
    stateNotes: [
      'Advisory group must include at least 1 physician and 1 RN',
      'Majority of advisory group must be non-owners/non-employees',
      'Advisory group must meet annually and review agency policies',
      'Must review: scope of services, admission/discharge policies, medical supervision, plans of care, emergency care, clinical records, personnel qualifications, program evaluation',
    ],
  },

  {
    id: 'WI-A3',
    itemId: 'A3',
    stateCode: 'WI',
    title: 'Administrator and Substitute Administrator',
    description: 'Wisconsin requires a qualified administrator and substitute administrator per DHS 133.06',
    severity: 'high',
    regulationCode: 'DHS 133.06',
    regulationName: 'Administration',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated administrator who meets Wisconsin qualification requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Administrator qualifications in DHS 133.06',
      },
      {
        id: 'q2',
        question: 'Do you have a designated substitute administrator who meets qualification requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Substitute administrator required per DHS 133.05(1)(e) and DHS 133.03(3)(h)',
      },
      {
        id: 'q3',
        question: 'Do you notify DHS within 10 days of any change in administrator?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not applicable' },
        ],
        helpText: 'Required per DHS 133.03(6)(a)',
      },
    ],
    scoringRules: [
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator and substitute administrator meet DHS 133.06 requirements.',
      atRisk: 'Administrator notification requirements may not be followed.',
      nonCompliant: 'Missing administrator or substitute administrator violates DHS 133.05(1)(e) and DHS 133.06.',
      unknown: 'Verify administrator qualifications with DHS.',
    },
    stateNotes: [
      'Administrator qualifications defined in DHS 133.06',
      'Substitute administrator required per DHS 133.05(1)(e)',
      'Must notify DHS within 10 days of administrator change per DHS 133.03(6)(a)',
      'Administrator responsible for day-to-day operations',
      'Must be available during normal business hours',
    ],
  },

  {
    id: 'WI-A4',
    itemId: 'A4',
    stateCode: 'WI',
    title: 'Program Evaluation Requirements',
    description: 'Wisconsin requires documented program evaluation per DHS 133.07',
    severity: 'medium',
    regulationCode: 'DHS 133.07',
    regulationName: 'Evaluation',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency conduct program evaluation that includes:',
        inputType: 'checklist',
        options: [
          { value: 'patient_care', label: 'Evaluation of patient care services' },
          { value: 'outcomes', label: 'Review of patient outcomes' },
          { value: 'policies', label: 'Annual review of policies and procedures' },
          { value: 'utilization', label: 'Utilization review' },
          { value: 'staff_performance', label: 'Staff performance evaluation' },
          { value: 'documentation', label: 'Documentation of evaluation findings' },
        ],
        required: true,
        helpText: 'Program evaluation required per DHS 133.07',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Program evaluation meets DHS 133.07 requirements.',
      atRisk: 'Program evaluation may be incomplete. Review DHS 133.07 requirements.',
      nonCompliant: 'Inadequate program evaluation violates DHS 133.07.',
      unknown: 'Review program evaluation requirements.',
    },
    stateNotes: [
      'Program evaluation required per DHS 133.07',
      'Advisory group must review agency policies annually',
      'Must evaluate patient care services and outcomes',
      'Utilization review and staff performance evaluation required',
      'Document all evaluation findings and corrective actions',
    ],
  },

  {
    id: 'WI-A5',
    itemId: 'A5',
    stateCode: 'WI',
    title: 'Services Provided',
    description: 'Wisconsin requires specific services to be provided or arranged per DHS 133.10',
    severity: 'high',
    regulationCode: 'DHS 133.10',
    regulationName: 'Services Provided',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency directly provide or arrange for the following required services?',
        inputType: 'checklist',
        options: [
          { value: 'skilled_nursing', label: 'Part-time or intermittent skilled nursing services' },
          { value: 'hha_services', label: 'Home health aide services' },
        ],
        required: true,
        helpText: 'Required services per DHS 133.10(1)',
      },
      {
        id: 'q2',
        question: 'Which optional therapeutic services does your agency provide? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'pt', label: 'Physical therapy' },
          { value: 'ot', label: 'Occupational therapy' },
          { value: 'st', label: 'Speech therapy' },
          { value: 'msw', label: 'Medical social services' },
        ],
        helpText: 'Optional services per DHS 133.10(2)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 1, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency provides required services per DHS 133.10.',
      atRisk: 'Agency may not provide all required services. Must provide or arrange for skilled nursing and home health aide services.',
      nonCompliant: 'Not providing required services violates DHS 133.10(1).',
      unknown: 'Verify services provided meet DHS 133.10 requirements.',
    },
    stateNotes: [
      'Must provide or arrange skilled nursing and home health aide services per DHS 133.10(1)',
      'May also provide PT, OT, ST, and medical social services',
      'Can contract with other providers for therapy services per DHS 133.19',
      'All services must be under physician orders',
      'Services must be part of an established plan of care',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS
  // ============================================
  {
    id: 'WI-B1',
    itemId: 'B1',
    stateCode: 'WI',
    title: 'Caregiver Background Check Program',
    description: 'Wisconsin requires background checks through the Caregiver Background Check Program per Wis. Stat. § 50.065 and DHS 12',
    severity: 'high',
    regulationCode: '§ 50.065 / DHS 12',
    regulationName: 'Caregiver Background Check Requirements',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 261-8319',
    agencyUrl: 'https://www.dhs.wisconsin.gov/misconduct/backgroundchecks.htm',
    questions: [
      {
        id: 'q1',
        question: 'For ALL caregivers, which background check components do you complete? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'bid', label: 'Background Information Disclosure (BID) form F-82064' },
          { value: 'doj_worcs', label: 'Wisconsin DOJ criminal history via WORCS' },
          { value: 'nurse_aide_registry', label: 'Nurse Aide Registry check' },
          { value: 'oig', label: 'OIG exclusion list check' },
          { value: 'sex_offender', label: 'Sex offender registry check' },
        ],
        required: true,
        helpText: 'Caregiver background check required per Wis. Stat. § 50.065 and DHS 12',
      },
      {
        id: 'q2',
        question: 'Do any caregivers begin working with patients BEFORE their background check is completed?',
        inputType: 'select',
        options: [
          { value: 'never', label: 'Never - always cleared before patient contact' },
          { value: 'clean_bid', label: 'Only with clean BID pending DOJ results (up to 60 days)' },
          { value: 'sometimes', label: 'Sometimes start before any clearance' },
        ],
        required: true,
        helpText: 'Clean BID allows up to 60 days pending DOJ results per DHS 12',
      },
      {
        id: 'q3',
        question: 'Do you renew background checks every 4 years as required?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'Background checks required at hire and every 4 years per DHS 12',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'includes', questionId: 'q2', value: ['never', 'clean_bid'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check process meets Wisconsin Caregiver Program requirements per Wis. Stat. § 50.065 and DHS 12.',
      atRisk: 'Background check process may have gaps. Must complete BID, DOJ check, and renew every 4 years.',
      nonCompliant: 'Caregivers working without proper background checks violates Wis. Stat. § 50.065. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Audit background check process against DHS 12 requirements.',
    },
    stateNotes: [
      'BID form F-82064 required before employment',
      'Clean BID allows work for up to 60 days pending DOJ results',
      'Background checks through Wisconsin DOJ WORCS system - $15 per check',
      'Required at hire and every 4 years thereafter',
      'Must check for barred offenses per DHS 12 Tables I and II',
      'Contact Office of Caregiver Quality: DHSCaregiverIntake@dhs.wisconsin.gov or (608) 261-8319',
    ],
  },

  {
    id: 'WI-B2',
    itemId: 'B2',
    stateCode: 'WI',
    title: 'Caregiver Misconduct Reporting',
    description: 'Wisconsin requires compliance with caregiver misconduct reporting per DHS 13',
    severity: 'high',
    regulationCode: 'DHS 133.06(4)(g) / DHS 13',
    regulationName: 'Caregiver Misconduct Reporting and Investigation',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 261-8319',
    agencyUrl: 'https://www.dhs.wisconsin.gov/misconduct/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have policies and procedures for reporting caregiver misconduct to DHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per DHS 133.06(4)(g) and DHS 13',
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse, neglect, or misappropriation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, trained on reporting procedures' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Caregiver misconduct reporting procedures meet DHS 13 requirements.',
      atRisk: 'Staff may not be adequately trained on misconduct reporting.',
      nonCompliant: 'No caregiver misconduct reporting procedures violates DHS 133.06(4)(g) and DHS 13.',
      unknown: 'Review caregiver misconduct reporting requirements.',
    },
    stateNotes: [
      'Caregiver misconduct reporting required per DHS 133.06(4)(g) and DHS 13',
      'Must report abuse, neglect, and misappropriation of property',
      'Report to Office of Caregiver Quality at (608) 261-8319',
      'Mandatory reporting requirement - failure to report may result in penalties',
      'Documentation of all incidents and reports required',
    ],
  },

  {
    id: 'WI-B3',
    itemId: 'B3',
    stateCode: 'WI',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to verify employees against OIG exclusion list',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion Requirements',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG Exclusion List (LEIE) for all employees before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct monthly OIG exclusion list checks for all employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Monthly checks recommended for Medicare/Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['sometimes', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed at hire and monthly.',
      atRisk: 'OIG checks at hire but monthly checks not implemented.',
      nonCompliant: 'No OIG exclusion list verification. This is required for Medicare/Medicaid providers.',
      unknown: 'Review OIG exclusion list verification requirements.',
    },
    stateNotes: [
      'OIG LEIE check required per 42 CFR 1001 for Medicare/Medicaid providers',
      'Free searchable database at exclusions.oig.hhs.gov',
      'Check before hire and monthly thereafter per OIG guidance',
      'Also check SAM.gov for vendors and contractors',
      'Document all checks with date, names checked, and results',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL
  // ============================================
  {
    id: 'WI-C1',
    itemId: 'C1',
    stateCode: 'WI',
    title: 'Personnel Records',
    description: 'Wisconsin requires specific personnel documentation per DHS 133.06',
    severity: 'high',
    regulationCode: 'DHS 133.06',
    regulationName: 'Administration - Personnel Requirements',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do you maintain in EVERY caregiver\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application' },
          { value: 'bid', label: 'Background Information Disclosure (BID) F-82064' },
          { value: 'doj_results', label: 'DOJ background check results' },
          { value: 'references', label: 'Reference verification' },
          { value: 'credentials', label: 'Professional credentials/licenses' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'training', label: 'Training and competency documentation' },
          { value: 'job_description', label: 'Job description' },
          { value: 'evaluations', label: 'Performance evaluations' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel records contain all required documentation per DHS 133.06.',
      atRisk: 'Personnel records may be missing required documents. Missing: {missing}.',
      nonCompliant: 'Personnel records missing critical documentation required by DHS 133.06.',
      unknown: 'Audit personnel files against DHS 133.06 requirements.',
    },
    stateNotes: [
      'Personnel records required per DHS 133.06',
      'BID form F-82064 and DOJ background check required per Wis. Stat. 50.065',
      'TB screening documentation required before patient contact',
      'Training and competency records must be maintained',
      'I-9 required under federal law within 3 days of hire',
    ],
  },

  {
    id: 'WI-C2',
    itemId: 'C2',
    stateCode: 'WI',
    title: 'Professional License Verification',
    description: 'Wisconsin requires verification of professional licenses for RNs, LPNs, therapists',
    severity: 'high',
    regulationCode: 'DHS 133.06 / DHS 133.14',
    regulationName: 'License Verification Requirements',
    agencyName: 'Wisconsin Department of Safety and Professional Services',
    agencyAbbr: 'DSPS',
    agencyPhone: '(608) 266-2112',
    agencyUrl: 'https://dsps.wi.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, OT, ST) before staff provide services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with DSPS before services' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and verify renewals?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with alerts' },
          { value: 'manual', label: 'Manual tracking' },
          { value: 'none', label: 'No active tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process is comprehensive.',
      atRisk: 'License verification is inconsistent. Implement systematic verification with DSPS.',
      nonCompliant: 'No license verification process violates Wisconsin professional practice requirements.',
      unknown: 'Review license verification process.',
    },
    stateNotes: [
      'License verification available at dsps.wi.gov',
      'RN licenses in Wisconsin renew every 2 years on even years',
      'LPN licenses renew every 2 years on even years',
      'Must verify license before hire and track renewal dates',
      'Wisconsin is a member of the Nurse Licensure Compact (NLC)',
    ],
  },

  // ============================================
  // SECTION D: HOME HEALTH AIDE SERVICES
  // ============================================
  {
    id: 'WI-D1',
    itemId: 'D1',
    stateCode: 'WI',
    title: 'Home Health Aide Qualifications',
    description: 'Wisconsin requires home health aides to meet qualification standards per DHS 133.17',
    severity: 'high',
    regulationCode: 'DHS 133.17',
    regulationName: 'Home Health Aide Services',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides meet Wisconsin qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'age', label: 'At least 18 years old' },
          { value: 'training', label: 'Completed approved training program (75 hours per 42 CFR 484)' },
          { value: 'competency', label: 'Passed competency evaluation' },
          { value: 'background', label: 'Clear caregiver background check' },
          { value: 'supervised', label: 'Works under RN supervision' },
        ],
        required: true,
        helpText: 'Requirements per DHS 133.17 and 42 CFR 484.80',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides meet Wisconsin qualification requirements per DHS 133.17.',
      atRisk: 'Some aides may not meet all qualification requirements. Review credentials immediately.',
      nonCompliant: 'Aides employed who do not meet qualification requirements. This violates DHS 133.17.',
      unknown: 'Verify all aide qualifications against DHS 133.17.',
    },
    stateNotes: [
      'Wisconsin follows federal 75-hour training requirement (42 CFR 484.80)',
      'Training must include classroom and supervised practical training',
      'Competency evaluation required before independent patient care',
      'Training directed by RN per DHS 133.17(4)(b)',
      '12 hours of in-service training required annually (42 CFR 484.80)',
    ],
  },

  {
    id: 'WI-D2',
    itemId: 'D2',
    stateCode: 'WI',
    title: 'Home Health Aide Training',
    description: 'Wisconsin requires 75 hours of training and annual in-service per DHS 133.17 and 42 CFR 484.80',
    severity: 'high',
    regulationCode: 'DHS 133.17 / 42 CFR 484.80',
    regulationName: 'Home Health Aide Training Requirements',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides complete required training before patient care?',
        inputType: 'checklist',
        options: [
          { value: 'classroom', label: 'Minimum 75 hours total training' },
          { value: 'clinical', label: 'At least 16 hours supervised clinical training' },
          { value: 'skills', label: 'Skills training in required subject areas' },
          { value: 'competency', label: 'Competency evaluation by RN' },
          { value: 'documentation', label: 'Training documented in personnel file' },
        ],
        required: true,
        helpText: 'Training requirements per 42 CFR 484.80',
      },
      {
        id: 'q2',
        question: 'Do ALL home health aides complete 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: 'Annual in-service required per 42 CFR 484.80(d)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training program meets Wisconsin and federal requirements (75 hours initial, 12 hours annual).',
      atRisk: 'Some staff may be behind on training requirements. Ensure all complete 12 hours annually.',
      nonCompliant: 'Inadequate training program violates DHS 133.17 and 42 CFR 484.80.',
      unknown: 'Review training program against requirements.',
    },
    stateNotes: [
      '75 hours total training required per 42 CFR 484.80',
      'Minimum 16 hours supervised clinical training required',
      'RN-directed competency evaluation required before patient care',
      '12 hours annual in-service training required (42 CFR 484.80(d))',
      'Training must be documented in personnel file',
    ],
  },

  // ============================================
  // SECTION E: SUPERVISION
  // ============================================
  {
    id: 'WI-E1',
    itemId: 'E1',
    stateCode: 'WI',
    title: 'RN Supervisory Visits',
    description: 'Wisconsin requires specific RN supervisory visit frequencies per DHS 133.18',
    severity: 'high',
    regulationCode: 'DHS 133.18',
    regulationName: 'Supervisory Visits',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'For patients receiving skilled nursing care, how often does an RN conduct supervisory visits?',
        inputType: 'select',
        options: [
          { value: 'every_2_weeks', label: 'At least every 2 weeks (meets DHS 133.18(1))' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'rarely', label: 'Less frequently' },
        ],
        required: true,
        helpText: 'Required every 2 weeks per DHS 133.18(1)',
      },
      {
        id: 'q2',
        question: 'For patients receiving ONLY home health aide services (no skilled nursing), how often does an RN conduct supervisory visits?',
        inputType: 'select',
        options: [
          { value: 'every_60_days', label: 'At least every 60 days (meets DHS 133.18(2))' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'rarely', label: 'Less frequently or never' },
        ],
        required: true,
        helpText: 'Required every 60 days per DHS 133.18(2)',
      },
      {
        id: 'q3',
        question: 'Are supervisory visits documented in patient records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always documented' },
          { value: 'no', label: 'No / inconsistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: inadequate frequency OR not documented
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'rarely' },
        { condition: 'equals', questionId: 'q2', value: 'rarely' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT: meets all requirements
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_2_weeks' },
        { condition: 'equals', questionId: 'q2', value: 'every_60_days' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: supervision frequency slightly off
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'monthly' },
        { condition: 'equals', questionId: 'q2', value: 'quarterly' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'RN supervisory visits meet DHS 133.18 requirements (every 2 weeks for skilled nursing patients, every 60 days for HHA-only patients).',
      atRisk: 'Supervisory visit frequency may not meet DHS 133.18 requirements.',
      nonCompliant: 'Inadequate RN supervision violates DHS 133.18. Patients must receive supervisory visits at required frequencies.',
      unknown: 'Review supervisory visit frequency against DHS 133.18.',
    },
    stateNotes: [
      'Patients receiving skilled nursing: RN supervisory visit at least every 2 weeks (DHS 133.18(1))',
      'Patients receiving only HHA services: RN supervisory visit at least every 60 days (DHS 133.18(2))',
      'Visits may be when aide is present or absent',
      'Purpose: observe, assist, assess relationships, determine if goals are being met',
    ],
  },

  {
    id: 'WI-E2',
    itemId: 'E2',
    stateCode: 'WI',
    title: 'Skilled Nursing Services',
    description: 'Wisconsin requires skilled nursing services under RN supervision per DHS 133.14',
    severity: 'high',
    regulationCode: 'DHS 133.14',
    regulationName: 'Skilled Nursing Services',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Are skilled nursing services provided by or under the supervision of a registered nurse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per DHS 133.14(1)',
      },
      {
        id: 'q2',
        question: 'Is the RN providing supervision available for consultation during all hours services are provided?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no RN supervision OR RN not available during all hours
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT: RN supervision AND available during all hours
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Skilled nursing services meet DHS 133.14 requirements.',
      atRisk: 'RN supervision may need improvement.',
      nonCompliant: 'Skilled nursing services not under RN supervision OR RN not available during all service hours violates DHS 133.14(1).',
      unknown: 'Review skilled nursing supervision requirements.',
    },
    stateNotes: [
      'Skilled nursing must be provided by or under RN supervision per DHS 133.14(1)',
      'RN must be available for consultation during all service hours',
      'LPNs may provide skilled nursing under RN supervision',
      'Documentation of supervision required in patient records',
      'RN responsible for developing and overseeing nursing care plans',
    ],
  },

  // ============================================
  // SECTION F: PLAN OF CARE & PATIENT RIGHTS
  // ============================================
  {
    id: 'WI-F1',
    itemId: 'F1',
    stateCode: 'WI',
    title: 'Plan of Care Requirements',
    description: 'Wisconsin requires a written plan of care for each patient per DHS 133.20',
    severity: 'high',
    regulationCode: 'DHS 133.20',
    regulationName: 'Plan of Care',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Is an initial plan of care developed within 72 hours of patient acceptance?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 72 hours' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No formal timeline' },
        ],
        required: true,
        helpText: 'Initial plan required within 72 hours per DHS 133.20(1)',
      },
      {
        id: 'q2',
        question: 'Is the complete plan of care signed by the physician within 20 working days?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 20 working days' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Physician signature required within 20 working days per DHS 133.20(1)',
      },
      {
        id: 'q3',
        question: 'Do your plans of care include all Wisconsin-required elements?',
        inputType: 'checklist',
        options: [
          { value: 'goals', label: 'Measurable time-specific goals with benchmark dates' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'orders', label: 'Physician orders' },
          { value: 'review', label: 'Plan for periodic review and update' },
        ],
        helpText: 'Required elements per DHS 133.20(2)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q3', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q2', value: 'usually' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care process meets DHS 133.20 requirements (72-hour initial, 20-day physician signature).',
      atRisk: 'Plan of care timelines may not always be met. Initial within 72 hours, physician signature within 20 working days.',
      nonCompliant: 'Plan of care process does not meet DHS 133.20 requirements.',
      unknown: 'Review plan of care requirements under DHS 133.20.',
    },
    stateNotes: [
      'Initial plan within 72 hours of acceptance (DHS 133.20(1))',
      'Physician signature within 20 working days (DHS 133.20(1))',
      'Must include measurable time-specific goals with benchmark dates (DHS 133.20(2)(a))',
      'Developed in consultation with patient, agency staff, and physician',
    ],
  },

  {
    id: 'WI-F2',
    itemId: 'F2',
    stateCode: 'WI',
    title: 'Patient Rights',
    description: 'Wisconsin requires compliance with patient rights per DHS 133.08',
    severity: 'high',
    regulationCode: 'DHS 133.08',
    regulationName: 'Patient Rights',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ALL patients with written information about their rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Patient rights form F-62601 available from DHS',
      },
      {
        id: 'q2',
        question: 'Do you document that patients received and understood their rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, signed acknowledgment in file' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you have a complaint and grievance procedure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights process meets DHS 133.08 requirements.',
      atRisk: 'Patient rights may not be documented. Obtain signed acknowledgments.',
      nonCompliant: 'Not providing patient rights information violates DHS 133.08.',
      unknown: 'Review patient rights requirements under DHS 133.08.',
    },
    stateNotes: [
      'Patient rights required per DHS 133.08',
      'DHS form F-62601 available for patient rights',
      'Must be provided before services begin',
      'Signed acknowledgment required in patient file',
      'Complaint procedure must be explained to patients',
    ],
  },

  {
    id: 'WI-F3',
    itemId: 'F3',
    stateCode: 'WI',
    title: 'Acceptance and Discharge of Patients',
    description: 'Wisconsin requires policies for patient acceptance and discharge per DHS 133.09',
    severity: 'medium',
    regulationCode: 'DHS 133.09',
    regulationName: 'Acceptance and Discharge of Patients',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written policies for patient acceptance and discharge?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When unable to meet patient needs, do you refer patients to appropriate services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, per DHS 133.11' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Referral requirements per DHS 133.11',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Acceptance and discharge policies meet DHS 133.09 requirements.',
      atRisk: 'Referral process may need strengthening.',
      nonCompliant: 'No acceptance/discharge policies violates DHS 133.09.',
      unknown: 'Review acceptance and discharge requirements.',
    },
    stateNotes: [
      'Written acceptance and discharge policies required per DHS 133.09',
      'Must refer patients to appropriate services when unable to meet needs per DHS 133.11',
      'Document all referrals and discharge reasons',
      'Coordinate with other providers per DHS 133.12',
      'Ensure continuity of care during transitions',
    ],
  },

  // ============================================
  // SECTION G: MEDICAL RECORDS
  // ============================================
  {
    id: 'WI-G1',
    itemId: 'G1',
    stateCode: 'WI',
    title: 'Medical Record Requirements',
    description: 'Wisconsin requires comprehensive medical records per DHS 133.21',
    severity: 'high',
    regulationCode: 'DHS 133.21',
    regulationName: 'Medical Records',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/regulations/hha/regulations.htm',
    questions: [
      {
        id: 'q1',
        question: 'Is a medical record maintained for each patient that is:',
        inputType: 'checklist',
        options: [
          { value: 'complete', label: 'Completely and accurately documented' },
          { value: 'organized', label: 'Systematically organized' },
          { value: 'accessible', label: 'Readily accessible to authorized personnel' },
          { value: 'secure', label: 'Safeguarded against loss, destruction, or unauthorized use' },
        ],
        required: true,
        helpText: 'Requirements per DHS 133.21(1) and (2)',
      },
      {
        id: 'q2',
        question: 'Do you have written procedures for record control, use, and release?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per DHS 133.21(2)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medical records meet DHS 133.21 requirements.',
      atRisk: 'Medical records may need improvement in organization or security.',
      nonCompliant: 'Medical records do not meet DHS 133.21 requirements.',
      unknown: 'Review medical record requirements under DHS 133.21.',
    },
    stateNotes: [
      'Medical records required per DHS 133.21',
      'Must be completely and accurately documented',
      'Records must be systematically organized and readily accessible',
      'Must safeguard against loss, destruction, or unauthorized use',
      'Written procedures for record control, use, and release required',
    ],
  },

  {
    id: 'WI-G2',
    itemId: 'G2',
    stateCode: 'WI',
    title: 'Record Retention',
    description: 'Wisconsin requires retention of medical records',
    severity: 'medium',
    regulationCode: '§ 146.819',
    regulationName: 'Record Retention',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient medical records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer from last service' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Wisconsin generally requires 5-year retention for healthcare records',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Wisconsin requirements.',
      atRisk: 'Record retention may not meet Wisconsin requirements. Generally 5 years required.',
      nonCompliant: 'Inadequate record retention policy.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Wisconsin generally requires 5-year retention per Wis. Stat. 146.819',
      'Medicare requires minimum 5 years for certified agencies',
      'For minors, retain until age of majority plus standard retention period',
      'Electronic records must meet same retention requirements',
      'Have procedures for record transfer if agency closes',
    ],
  },

  // ============================================
  // SECTION H: HEALTH & SAFETY
  // ============================================
  {
    id: 'WI-H1',
    itemId: 'H1',
    stateCode: 'WI',
    title: 'TB Screening Requirements',
    description: 'Wisconsin requires TB screening for direct care workers',
    severity: 'high',
    regulationCode: 'DHS 133.06',
    regulationName: 'Health Screening Requirements',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care workers have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have current TB screening' },
          { value: 'most', label: 'Most have screening, some may be expired' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'TB testing before patient contact',
      },
      {
        id: 'q2',
        question: 'Do you track TB screening expiration dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking with reminders' },
          { value: 'manual', label: 'Manual tracking' },
          { value: 'none', label: 'No tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all direct care staff.',
      atRisk: 'Some TB screenings may be expired. Review all files.',
      nonCompliant: 'Direct care workers without valid TB screening creates health risks.',
      unknown: 'Verify TB screening status for all direct care workers.',
    },
    stateNotes: [
      'TB screening required per DHS 133.06 before patient contact',
      'Acceptable methods: TST skin test or IGRA blood test',
      'Positive results require chest X-ray and physician clearance',
      'Track screening expiration dates proactively',
      'Document results in personnel file',
    ],
  },

  {
    id: 'WI-H2',
    itemId: 'H2',
    stateCode: 'WI',
    title: 'Emergency Notification',
    description: 'Wisconsin requires prompt notification of changes in patient condition per DHS 133.13',
    severity: 'high',
    regulationCode: 'DHS 133.13',
    regulationName: 'Emergency Notification',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have policies for promptly notifying physicians of significant changes in patient condition?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per DHS 133.13',
      },
      {
        id: 'q2',
        question: 'Are staff trained on emergency notification procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency notification procedures meet DHS 133.13 requirements.',
      atRisk: 'Staff may need additional training on emergency notification.',
      nonCompliant: 'No emergency notification procedures violates DHS 133.13.',
      unknown: 'Review emergency notification requirements.',
    },
    stateNotes: [
      'Personnel must promptly notify patient\'s physician of significant changes',
      'Also notify guardian if applicable',
      'Document all notifications',
    ],
  },

  {
    id: 'WI-H3',
    itemId: 'H3',
    stateCode: 'WI',
    title: 'Infection Control',
    description: 'Wisconsin requires infection control policies and training per DHS 133.06(5)',
    severity: 'high',
    regulationCode: 'DHS 133.06(5)',
    regulationName: 'Infection Control and Prevention',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection control program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per DHS 133.06(5)',
      },
      {
        id: 'q2',
        question: 'Do all staff receive infection control training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at orientation and annually' },
          { value: 'initial_only', label: 'At orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'initial_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets DHS 133.06(5) requirements.',
      atRisk: 'Infection control training should be conducted annually.',
      nonCompliant: 'No infection control program violates DHS 133.06(5).',
      unknown: 'Review infection control requirements.',
    },
    stateNotes: [
      'Written infection control program required per DHS 133.06(5)',
      'Must include hand hygiene, PPE, and standard precautions',
      'Training required during orientation and annually thereafter',
      'Bloodborne pathogens training required under OSHA',
      'Document all infection control training',
    ],
  },

  // ============================================
  // SECTION I: ELECTRONIC VISIT VERIFICATION
  // ============================================
  {
    id: 'WI-I1',
    itemId: 'I1',
    stateCode: 'WI',
    title: 'Electronic Visit Verification (EVV)',
    description: 'Wisconsin requires EVV for Medicaid-funded personal care and home health services',
    severity: 'high',
    regulationCode: '21st Century Cures Act / Wisconsin Medicaid EVV Requirements',
    regulationName: 'EVV',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/evv/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care or home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a Wisconsin-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all required visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'EVV required for Medicaid personal care (effective 2020) and home health services (effective January 1, 2023)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Wisconsin Medicaid services.',
      atRisk: 'EVV is not being used consistently for all required visits. Ensure 100% EVV compliance.',
      nonCompliant: 'Wisconsin Medicaid services require EVV per federal law. Contact DHS EVV program.',
      unknown: 'If you provide Medicaid services, verify EVV compliance.',
    },
    stateNotes: [
      'EVV required for Medicaid personal care services (effective 2020)',
      'EVV required for Medicaid home health services (effective January 1, 2023)',
      'Must verify: type of service, individual receiving service, date, location, individual providing service, time started/ended',
      'More information: https://www.dhs.wisconsin.gov/evv/',
    ],
  },

  {
    id: 'WI-I2',
    itemId: 'I2',
    stateCode: 'WI',
    title: 'Services Under Contract',
    description: 'Wisconsin requires written contracts for services purchased from other providers per DHS 133.19',
    severity: 'medium',
    regulationCode: 'DHS 133.19',
    regulationName: 'Services Under Contract',
    agencyName: 'Wisconsin Department of Health Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(608) 266-8481',
    agencyUrl: 'https://www.dhs.wisconsin.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you contract with other providers for health care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have written contracts for all contracted services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Written contracts required per DHS 133.19(1)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracted services meet DHS 133.19 requirements.',
      atRisk: 'N/A',
      nonCompliant: 'No written contracts for contracted services violates DHS 133.19(1).',
      unknown: 'Review contracted services requirements.',
    },
    stateNotes: [
      'Written contracts required per DHS 133.19(1) for all contracted services',
      'Contracts must specify services to be provided',
      'Quality standards and reporting requirements must be included',
      'Agency retains administrative responsibility for contracted services',
      'Contract services must meet same standards as direct services',
    ],
  },
];

export default WISCONSIN_QUESTIONS;